import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, LinearProgress, Button, Alert, Chip } from '@mui/material';
import { useSocket } from '@/context/SocketContext';
import api from '../../services/api'; // Assuming api service exists

interface JobState {
    jobId: string;
    type: string;
    status: 'IDLE' | 'RUNNING' | 'COMPLETED' | 'ERROR';
    progress: number;
    details: string;
    error?: string;
}

export const SystemStatusWidget: React.FC = () => {
    const { socket } = useSocket();
    const [jobState, setJobState] = useState<JobState>({
        jobId: '',
        type: 'INGEST_USDA',
        status: 'IDLE',
        progress: 0,
        details: 'System Ready'
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch initial status
    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const { data } = await api.get('/admin/jobs/status');
                setJobState(data);
            } catch (err) {
                console.error('Failed to fetch job status', err);
            }
        };
        fetchStatus();
    }, []);

    // Listen for WebSocket updates
    useEffect(() => {
        if (!socket) return;

        // Join the ops room
        socket.emit('join_ops');

        const handleUpdate = (newState: JobState) => {
            // console.debug('Job Update:', newState);
            setJobState(newState);
        };

        socket.on('job:update', handleUpdate);

        return () => {
            socket.off('job:update', handleUpdate);
        };
    }, [socket]);

    const handleStartIngestion = async () => {
        setLoading(true);
        setError(null);
        try {
            await api.post('/admin/jobs/start', { type: 'INGEST_USDA' });
            // State will update via WebSocket
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to start job');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'RUNNING': return 'primary';
            case 'COMPLETED': return 'success';
            case 'ERROR': return 'error';
            default: return 'default';
        }
    };

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" component="div">
                        System Operations
                    </Typography>
                    <Chip
                        label={jobState.status}
                        color={getStatusColor(jobState.status) as any}
                        size="small"
                    />
                </Box>

                <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        USDA Ingestion Status
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={jobState.progress}
                        sx={{ height: 10, borderRadius: 5, mb: 1 }}
                        color={jobState.status === 'ERROR' ? 'error' : 'primary'}
                    />
                    <Typography variant="caption" display="block" align="right">
                        {jobState.progress}% | {jobState.details}
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleStartIngestion}
                    disabled={jobState.status === 'RUNNING' || loading}
                >
                    {jobState.status === 'RUNNING' ? 'Ingestion in Progress...' : 'Start FDA Ingestion'}
                </Button>
            </CardContent>
        </Card>
    );
};
